import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Descriptions, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useStore } from '@hub/helpers/use-store';
import { Card, Statistic } from '@monorepo/react-components';

interface Props {
  equity: number;
  allowedDailyLosses: number;
  snapshotBalance?: number;
  snapshotEquity?: number;
  programId: string;
}

const AccountDailyProfitsAndLosses: FunctionComponent<Props> = ({
  equity,
  allowedDailyLosses,
  snapshotBalance,
  snapshotEquity,
  programId,
}) => {
  const {
    dataStore: { programStore },
  } = useStore();
  const { t } = useTranslation(['common', 'dashboardTemplates']);

  const program = programStore.get(programId);

  const maxDailyLoss =
    program?.config?.dailyLossPercentage ||
    program?.config?.dailyPausePercentage ||
    0;

  const titleText = program?.config?.dailyLossPercentage
    ? t('dashboardTemplates:daily_loss')
    : t('dashboardTemplates:daily_pause');

  const allowedLoss = allowedDailyLosses * -1;
  const getMaxSnapshot = () => {
    if (!snapshotBalance || !snapshotEquity) {
      return 0;
    }

    return Math.max(snapshotBalance, snapshotEquity);
  };

  return (
    <Card
      title={titleText}
      extra={
        <Tooltip
          title={t('dashboardTemplates:max_daily_loss_title', {
            value: maxDailyLoss,
          })}
        >
          <InfoCircleOutlined />
        </Tooltip>
      }
    >
      <Descriptions column={1}>
        <Descriptions.Item label={t('dashboardTemplates:current_level')}>
          <Statistic prefix={'$'} value={equity} precision={2} />
        </Descriptions.Item>
        <Descriptions.Item label={t('dashboardTemplates:min_level')}>
          <Statistic
            prefix={'$'}
            value={getMaxSnapshot() - allowedLoss}
            precision={2}
          />
        </Descriptions.Item>
        {snapshotBalance ? (
          <Descriptions.Item label={t('dashboardTemplates:midnight_balance')}>
            <Statistic prefix={'$'} value={snapshotBalance} precision={2} />
          </Descriptions.Item>
        ) : null}
        {snapshotEquity ? (
          <Descriptions.Item label={t('dashboardTemplates:midnight_equity')}>
            <Statistic prefix={'$'} value={snapshotEquity} precision={2} />
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    </Card>
  );
};

export default AccountDailyProfitsAndLosses;
